import {
    AnalyzeAdsAcountAd,
    DynamicPageVariant,
    GoogleAdsAdGroup,
    GoogleAdsCampaign,
    GoogleAdsLandingPage,
} from '@superfunnel/types';
import Loader from '@superfunnel/ui-core/Loader';
import { Handle, Position } from '@xyflow/react';
import { useHasFeature } from '../../hooks/useHasFeature';
import { classNames } from '../../utils';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const numberFormatter = new Intl.NumberFormat('en-US');

const percentageFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 2,
});

// Campaign Node
const CampaignNode = ({
  data,
}: {
  data: { label: string; metrics: GoogleAdsCampaign['metrics'] };
}) => {
  const hasFinancialDataFeature = useHasFeature('viewFinancialData');

  const { label, metrics } = data;

  return (
    <div className="w-[300px] rounded-lg border-2 border-blue-500 bg-white p-4 shadow-lg">
      <Handle type="target" position={Position.Left} />
      <div className="absolute -top-8 left-0">
        <strong>Campaign</strong>
      </div>
      <div className="mb-2 text-lg font-bold text-blue-600">{label}</div>
      <div className="space-y-1 text-sm">
        {hasFinancialDataFeature && (
          <div className="flex justify-between">
            <span className="text-gray-600">Cost:</span>
            <span className="font-medium">
              {currencyFormatter.format(metrics?.cost ?? 0)}
            </span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-gray-600">Clicks:</span>
          <span className="font-medium">
            {numberFormatter.format(metrics?.clicks ?? 0)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Conv Rate:</span>
          <span className="font-medium">
            {percentageFormatter.format(
              (metrics?.conversions ?? 0) / (metrics?.clicks || 1),
            )}
          </span>
        </div>
      </div>
      <Handle type="source" position={Position.Right} />
    </div>
  );
};

// Ad Group Node
const AdGroupNode = ({
  data,
}: {
  data: { label: string; metrics: GoogleAdsAdGroup['metrics'] };
}) => {
  const hasFinancialDataFeature = useHasFeature('viewFinancialData');
  const { label, metrics } = data;

  return (
    <div className="w-[300px] rounded-lg border-2 border-green-500 bg-white p-4 shadow-lg">
      <Handle type="target" position={Position.Left} />
      <div className="absolute -top-8 left-0">
        <strong>Ad Group</strong>
      </div>
      <div className="mb-2 text-lg font-bold text-green-600">{label}</div>
      <div className="space-y-1 text-sm">
        {hasFinancialDataFeature && (
          <div className="flex justify-between">
            <span className="text-gray-600">Cost:</span>
            <span className="font-medium">
              {currencyFormatter.format(metrics?.cost ?? 0)}
            </span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-gray-600">Clicks:</span>
          <span className="font-medium">
            {numberFormatter.format(metrics?.clicks ?? 0)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Conv Rate:</span>
          <span className="font-medium">
            {percentageFormatter.format(
              (metrics?.conversions ?? 0) / (metrics?.clicks || 1),
            )}
          </span>
        </div>
      </div>
      <Handle type="source" position={Position.Right} />
    </div>
  );
};

const getDomain = (fullUrl: string) => {
  try {
    return new URL(fullUrl).hostname.replace('www.', '');
  } catch {
    return fullUrl;
  }
};

// Ad Node
const AdNode = ({
  data,
}: {
  data: AnalyzeAdsAcountAd & {
    label: string;
    headlines: string[];
    descriptions: string[];
    lowPerforming: boolean;
  };
}) => {
  const hasFinancialDataFeature = useHasFeature('viewFinancialData');

  const { metrics, headlines, descriptions, lowPerforming, score } = data;

  const finalUrls = 'finalUrls' in data ? data.finalUrls : [];

  const _score = score
    ? typeof score === 'string'
      ? +(score as string).split('/')[0]
      : score
    : undefined;

  const scoreColorTWClass = _score
    ? _score < 5
      ? 'bg-red-500'
      : _score < 7
        ? 'bg-yellow-500'
        : 'bg-green-500'
    : 'bg-gray-500';

  return (
    <div
      className={classNames(
        'w-[400px] rounded-lg border-2 border-purple-500 bg-white p-4 shadow-lg',
        lowPerforming ? 'bg-red-100' : '',
      )}
    >
      <Handle type="target" position={Position.Left} />
      <div className="absolute -top-9 left-0">
        <strong>Ad</strong>
      </div>
      {/* badge that floats on the right top of the card with a text, blue bg */}
      <div className="absolute -top-3 right-3 flex flex-row gap-3">
        <div
          className={classNames(
            'flex gap-2 rounded-sm p-1 text-xs font-bold text-white',
            scoreColorTWClass,
          )}
        >
          {'Ad-to-Page Relevancy - '}
          {score ? `${(10 * score).toFixed(1)}/10` : <Loader size={4} />}
        </div>
        {metrics?.clicks === 0 ? (
          <div className="rounded bg-yellow-500 p-1 text-xs font-bold text-white">
            Learning
          </div>
        ) : (
          <div className="rounded bg-blue-500 p-1 text-xs font-bold text-white">
            {lowPerforming ? 'Low Performing' : 'High Performing'}
          </div>
        )}
      </div>
      <div className="max-w-2xl">
        <div className="flex flex-col">
          {/* {headlines?.slice(0, 3).map((h) => (
            <a
              href={finalUrls[0]}
              className="hover:underline"
              target="_blank"
              rel="noreferrer"
              key={h}
            >
              <h3 className="line-clamp-1 text-xl font-normal text-blue-800">
                {h}
              </h3>
            </a>
          ))} */}
          <a
            href={finalUrls[0]}
            className="hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            <h3 className="line-clamp-3 text-xl font-normal text-blue-800">
              {(headlines as any[])
                .filter((h) => h.text)
                .sort((a, b) => b.metrics.impressions - a.metrics.impressions)
                .map((h) => h.text)
                .slice(0, 3)
                .join(' | ')}
            </h3>
          </a>
          <div className="mb-1 text-sm text-gray-600">
            {getDomain(finalUrls[0])}
          </div>
          {(descriptions as any[]).slice(0, 1).map((d) => (
            <p className="line-clamp-2 text-sm text-gray-900" key={d.text}>
              {d.text}
            </p>
          ))}
        </div>
      </div>
      {/* <div className="mb-2 text-lg font-bold text-purple-600">{label}</div> */}
      <div className="mt-1 flex gap-10 pt-2">
        <div className="flex-1 space-y-1 text-sm">
          {hasFinancialDataFeature && (
            <div className="flex justify-between">
              <span className="text-gray-600">Cost:</span>
              <span className="font-medium">
                {currencyFormatter.format(metrics?.cost ?? 0)}
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-600">Clicks:</span>
            <span className="font-medium">
              {numberFormatter.format(metrics?.clicks ?? 0)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">CVR:</span>
            <span className="font-medium">
              {percentageFormatter.format(
                (metrics?.conversions ?? 0) / (metrics?.clicks || 1),
              )}
            </span>
          </div>
        </div>

        <div className="flex-1 space-y-1 text-sm">
          {hasFinancialDataFeature && (
            <div className="flex justify-between">
              <span className="text-gray-600">CPC:</span>
              <span className="font-medium">
                {currencyFormatter.format(metrics?.avgCpc ?? 0)}
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-600">CTR:</span>
            <span className="font-medium">
              {percentageFormatter.format(metrics?.ctr ?? 0)}
            </span>
          </div>
          {hasFinancialDataFeature && (
            <div className="flex justify-between">
              <span className="text-gray-600">CPL:</span>
              <span className="font-medium">
                {currencyFormatter.format(metrics?.costPerConversion ?? 0)}
              </span>
            </div>
          )}
        </div>
      </div>
      <Handle type="source" position={Position.Right} />
    </div>
  );
};

// Landing Page Node
const LandingPageNode = ({
  data,
}: {
  data: { label: string; metrics: GoogleAdsLandingPage['metrics'] };
}) => {
  const hasFinancialDataFeature = useHasFeature('viewFinancialData');

  const { label, metrics } = data;

  return (
    <div className="w-[300px] rounded-lg border-2 border-orange-500 bg-white p-4 shadow-lg">
      <Handle type="target" position={Position.Left} />
      <div className="absolute -top-8 left-0">
        <strong>Landing Page</strong>
      </div>
      <div className="mb-2 text-lg font-bold text-orange-600">{label}</div>
      <div className="space-y-1 text-sm">
        {hasFinancialDataFeature && (
          <div className="flex justify-between">
            <span className="text-gray-600">Cost:</span>
            <span className="font-medium">
              {currencyFormatter.format(metrics?.cost ?? 0)}
            </span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-gray-600">Clicks:</span>
          <span className="font-medium">
            {numberFormatter.format(metrics?.clicks ?? 0)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Speed Score:</span>
          <span className="font-medium">
            {numberFormatter.format(metrics?.speedScore ?? 0)}
          </span>
        </div>
      </div>
    </div>
  );
};

// Landing Page Node
const SmartVariantNode = ({
  data,
}: {
  data: { label: string } & DynamicPageVariant;
}) => {
  const { label } = data;

  return (
    <div className="w-[300px] rounded-lg border-2 border-orange-500 bg-white p-4 shadow-lg">
      <Handle type="target" position={Position.Left} />
      <div className="absolute -top-8 left-0">
        <strong>Variant, Smart Traffic</strong>
      </div>
      <div className="mb-2 text-lg font-bold text-orange-600">{label}</div>
      {/* <div className="space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">Cost:</span>
          <span className="font-medium">
            {currentFormatter.format(metrics?.cost ?? 0)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Clicks:</span>
          <span className="font-medium">
            {numberFormatter.format(metrics?.clicks ?? 0)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Speed Score:</span>
          <span className="font-medium">
            {numberFormatter.format(metrics?.speedScore ?? 0)}
          </span>
        </div>
      </div> */}
    </div>
  );
};

// Account Node (root node)
const AccountNode = ({ data }: any) => {
  const { label } = data;

  return (
    <div className="w-[300px] rounded-lg border-2 border-gray-500 bg-white p-4 shadow-lg">
      <div className="mb-2 text-lg font-bold text-gray-600">{label}</div>
      <Handle type="source" position={Position.Right} />
      <div className="absolute -top-8 left-0">
        <strong>Ads Account</strong>
      </div>
    </div>
  );
};

const Loading = ({
  data,
}: {
  data: { label: string; showTargetHandle: boolean; showSourceHandle: boolean };
}) => {
  return (
    <div className="w-[300px] rounded-lg border-2 border-gray-500 bg-white p-4 shadow-lg">
      {data.showTargetHandle && (
        <Handle type="target" position={Position.Left} />
      )}

      <div className="flex w-full items-center gap-3">
        <Loader size={6} />
        <strong>{data.label}</strong>
      </div>
      {data.showSourceHandle && (
        <Handle type="source" position={Position.Right} />
      )}
    </div>
  );
};

// Node types object to register with ReactFlow
export const nodeTypes = {
  campaign: CampaignNode,
  adgroup: AdGroupNode,
  ad: AdNode,
  landingPage: LandingPageNode,
  account: AccountNode,
  variant: SmartVariantNode,
  loading: Loading,
};
